import React, { useState } from 'react';
import { Mail, Star, CheckCircle, Shield, TrendingUp, DollarSign, BarChart3, X } from 'lucide-react';

const NewsletterSignup: React.FC = () => {
  const [formData, setFormData] = useState({
    email: '',
    interests: [] as string[],
    frequency: 'weekly'
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    console.log('Form submitted:', formData);
    setSubmitted(true);
  };

  // Function to handle closing the success message
  const handleCloseSuccess = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSubmitted(false);
  };

  const topics = [
    'Market Analysis',
    'Retirement Planning',
    'Tax Strategies',
    'Investment Tips',
    'Estate Planning',
    'Social Security'
  ];

  const benefits = [
    {
      icon: <TrendingUp className="h-12 w-12 text-primary" />,
      title: 'Market Insights',
      description: 'Stay ahead with our expert analysis of market trends and opportunities'
    },
    {
      icon: <DollarSign className="h-12 w-12 text-primary" />,
      title: 'Investment Strategies',
      description: 'Learn proven strategies to grow and protect your wealth'
    },
    {
      icon: <BarChart3 className="h-12 w-12 text-primary" />,
      title: 'Performance Updates',
      description: 'Regular updates on portfolio performance and market conditions'
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">
            Get Expert Financial Insights Delivered to Your Inbox
          </h1>
          <p className="text-xl text-gray-600">
            Join thousands of investors who trust our research and analysis
          </p>
        </div>

        {!submitted ? (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Form Section */}
            <div className="bg-white rounded-xl shadow-sm p-8">
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <div className="mt-1 relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="pl-10 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                      placeholder="you@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Topics of Interest
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {topics.map((topic) => (
                      <label
                        key={topic}
                        className="flex items-center space-x-2 p-3 border rounded-lg hover:border-primary/50 cursor-pointer"
                      >
                        <input
                          type="checkbox"
                          checked={formData.interests.includes(topic)}
                          onChange={(e) => {
                            const newInterests = e.target.checked
                              ? [...formData.interests, topic]
                              : formData.interests.filter(t => t !== topic);
                            setFormData({ ...formData, interests: newInterests });
                          }}
                          className="rounded text-primary focus:ring-primary"
                        />
                        <span className="text-sm">{topic}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Email Frequency
                  </label>
                  <select
                    value={formData.frequency}
                    onChange={(e) => setFormData({ ...formData, frequency: e.target.value })}
                    className="mt-1 block w-full border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  >
                    <option value="daily">Daily Updates</option>
                    <option value="weekly">Weekly Digest</option>
                    <option value="monthly">Monthly Summary</option>
                  </select>
                </div>

                <button type="submit" className="w-full btn btn-primary">
                  Subscribe Now
                </button>

                <div className="text-center">
                  <div className="inline-flex items-center text-sm text-gray-500">
                    <Shield className="h-4 w-4 mr-2" />
                    We respect your privacy and will never share your information
                  </div>
                </div>
              </form>
            </div>

            {/* Benefits Section */}
            <div className="space-y-8">
              {benefits.map((benefit, index) => (
                <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="mb-4">
                    {benefit.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{benefit.title}</h3>
                  <p className="text-gray-600">{benefit.description}</p>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-xl font-semibold">Subscription Confirmed</h3>
                <button 
                  onClick={handleCloseSuccess}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="text-center mb-6">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-gray-900 mb-2">Thank You for Subscribing!</h2>
                <p className="text-gray-600">
                  You've been added to our newsletter. Watch your inbox for valuable financial insights and market updates.
                </p>
              </div>
              <div className="bg-blue-50 rounded-lg p-4 mb-6">
                <div className="flex">
                  <Mail className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
                  <div>
                    <h4 className="text-sm font-medium text-blue-800">Next Steps</h4>
                    <p className="mt-1 text-sm text-blue-700">
                      Please check your email to confirm your subscription. If you don't see it in your inbox, please check your spam folder.
                    </p>
                  </div>
                </div>
              </div>
              <button
                onClick={handleCloseSuccess}
                className="w-full btn btn-primary"
              >
                Return to Newsletter Page
              </button>
            </div>
          </div>
        )}

        {/* Testimonials */}
        <div className="mt-16">
          <h2 className="text-2xl font-semibold text-center mb-8">
            What Our Subscribers Say
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: "The market insights have been invaluable for my investment decisions.",
                author: "Michael R.",
                role: "Retired Professional"
              },
              {
                quote: "Clear, actionable advice that has helped me grow my portfolio consistently.",
                author: "Sarah J.",
                role: "Business Owner"
              },
              {
                quote: "The weekly updates keep me informed without overwhelming me with information.",
                author: "David P.",
                role: "Private Investor"
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-sm">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-gray-600 mb-4">"{testimonial.quote}"</p>
                <div>
                  <p className="font-semibold">{testimonial.author}</p>
                  <p className="text-sm text-gray-500">{testimonial.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Features List */}
        <div className="mt-16">
          <div className="bg-white rounded-xl shadow-sm p-8">
            <h2 className="text-2xl font-semibold text-center mb-8">
              Everything You Need to Make Informed Decisions
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                'Expert market analysis and commentary',
                'Portfolio optimization strategies',
                'Tax planning insights',
                'Retirement planning tips',
                'Investment opportunity alerts',
                'Economic trend analysis',
                'Risk management strategies',
                'Asset allocation guidance'
              ].map((feature, index) => (
                <div key={index} className="flex items-start">
                  <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                  <span>{feature}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsletterSignup;